import { useState, useRef, useCallback } from 'react';
import { Camera, Download, Upload, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const GRADIENTS = [
    { name: 'Sunset', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    { name: 'Ocean', value: 'linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)' },
    { name: 'Fire', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    { name: 'Forest', value: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
    { name: 'Night', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
    { name: 'Peach', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' },
    { name: 'Purple', value: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)' },
    { name: 'Dark', value: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
    { name: 'Blue', value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
    { name: 'Rose', value: 'linear-gradient(135deg, #f43b47 0%, #453a94 100%)' },
    { name: 'Lime', value: 'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)' },
    { name: 'Midnight', value: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)' },
];

const ScreenshotBeautifier = () => {
    const [image, setImage] = useState<string | null>(null);
    const [selectedGradient, setSelectedGradient] = useState(GRADIENTS[0].value);
    const [padding, setPadding] = useState([60]);
    const [borderRadius, setBorderRadius] = useState([16]);
    const [shadow, setShadow] = useState([40]);
    const canvasRef = useRef<HTMLDivElement>(null);

    const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        const url = URL.createObjectURL(file);
        setImage(url);
        toast.success('Image uploaded!');
    }, []);

    const downloadImage = useCallback(async () => {
        if (!canvasRef.current || !image) {
            toast.error('No image to download');
            return;
        }

        try {
            // Create canvas for export
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Canvas not supported');

            const img = new Image();
            img.crossOrigin = 'anonymous';

            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = image;
            });

            const paddingPx = padding[0];
            const canvasWidth = img.width + paddingPx * 2;
            const canvasHeight = img.height + paddingPx * 2;

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            // Draw gradient background
            const gradient = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
            const gradientMatch = selectedGradient.match(/linear-gradient\(.*?,\s*(.+?)\s+\d+%,\s*(.+?)\s+\d+%(?:,\s*(.+?)\s+\d+%)?\)/);

            if (gradientMatch) {
                gradient.addColorStop(0, gradientMatch[1]);
                gradient.addColorStop(1, gradientMatch[2]);
            } else {
                gradient.addColorStop(0, '#667eea');
                gradient.addColorStop(1, '#764ba2');
            }

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);

            // Draw shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
            ctx.shadowBlur = shadow[0];
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = shadow[0] / 3;

            // Draw rounded rectangle for image
            const x = paddingPx;
            const y = paddingPx;
            const radius = borderRadius[0];

            ctx.beginPath();
            ctx.roundRect(x, y, img.width, img.height, radius);
            ctx.clip();
            ctx.drawImage(img, x, y);

            // Download
            const a = document.createElement('a');
            a.href = canvas.toDataURL('image/png');
            a.download = 'beautified-screenshot.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            toast.success('Downloaded!');
        } catch (error) {
            toast.error('Failed to download');
            console.error(error);
        }
    }, [image, padding, borderRadius, shadow, selectedGradient]);

    const resetSettings = () => {
        setPadding([60]);
        setBorderRadius([16]);
        setShadow([40]);
        setSelectedGradient(GRADIENTS[0].value);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 rounded-xl bg-primary/10">
                        <Camera className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold">Screenshot Beautifier</h1>
                        <p className="text-muted-foreground">Add beautiful gradient backgrounds to your screenshots</p>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                {/* Controls */}
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <div className="flex justify-between items-center">
                            <CardTitle>Settings</CardTitle>
                            <Button variant="ghost" size="sm" onClick={resetSettings}>
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Upload */}
                        <label className="block">
                            <div className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-primary transition-colors">
                                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm font-medium">
                                    {image ? 'Change Screenshot' : 'Upload Screenshot'}
                                </p>
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                            />
                        </label>

                        {/* Gradient Selection */}
                        <div>
                            <Label className="mb-3 block">Background</Label>
                            <div className="grid grid-cols-4 gap-2">
                                {GRADIENTS.map((g) => (
                                    <button
                                        key={g.name}
                                        onClick={() => setSelectedGradient(g.value)}
                                        className={`aspect-square rounded-lg transition-transform hover:scale-105 ${selectedGradient === g.value ? 'ring-2 ring-primary ring-offset-2' : ''
                                            }`}
                                        style={{ background: g.value }}
                                        title={g.name}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Padding */}
                        <div>
                            <div className="flex justify-between mb-2">
                                <Label>Padding</Label>
                                <span className="text-sm text-muted-foreground">{padding[0]}px</span>
                            </div>
                            <Slider
                                value={padding}
                                onValueChange={setPadding}
                                min={20}
                                max={150}
                                step={5}
                            />
                        </div>

                        {/* Border Radius */}
                        <div>
                            <div className="flex justify-between mb-2">
                                <Label>Border Radius</Label>
                                <span className="text-sm text-muted-foreground">{borderRadius[0]}px</span>
                            </div>
                            <Slider
                                value={borderRadius}
                                onValueChange={setBorderRadius}
                                min={0}
                                max={40}
                                step={2}
                            />
                        </div>

                        {/* Shadow */}
                        <div>
                            <div className="flex justify-between mb-2">
                                <Label>Shadow</Label>
                                <span className="text-sm text-muted-foreground">{shadow[0]}px</span>
                            </div>
                            <Slider
                                value={shadow}
                                onValueChange={setShadow}
                                min={0}
                                max={80}
                                step={5}
                            />
                        </div>

                        {/* Download */}
                        <Button
                            onClick={downloadImage}
                            className="w-full"
                            disabled={!image}
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </Button>
                    </CardContent>
                </Card>

                {/* Preview */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Preview</CardTitle>
                        <CardDescription>Live preview of your beautified screenshot</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div
                            ref={canvasRef}
                            className="rounded-xl overflow-hidden flex items-center justify-center min-h-[400px]"
                            style={{
                                background: selectedGradient,
                                padding: `${padding[0]}px`,
                            }}
                        >
                            {image ? (
                                <img
                                    src={image}
                                    alt="Screenshot"
                                    className="max-w-full max-h-[500px] object-contain"
                                    style={{
                                        borderRadius: `${borderRadius[0]}px`,
                                        boxShadow: `0 ${shadow[0] / 3}px ${shadow[0]}px rgba(0, 0, 0, 0.4)`,
                                    }}
                                />
                            ) : (
                                <div className="text-center text-white/70">
                                    <Camera className="h-16 w-16 mx-auto mb-4 opacity-50" />
                                    <p className="text-lg font-medium">Upload a screenshot to preview</p>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ScreenshotBeautifier;
